import {
  auroraDispatchEnvelopeSchema,
  type AuroraPushAction,
  auroraPushEnvelopeSchema,
  type AuroraPushEnvelope,
  type AuroraResponse,
  AuroraResponseMessageType,
  auroraResponseSchema,
  auroraSubmitDispatchResultApi,
  errorResponseSchema
} from 'api-validations/aurora'
import type { PanoramaWebSocketApi } from 'api-validations/websocket'
import { z } from 'zod'

import { ManagedWebsocket, type ManagedWebsocketConfig } from '../../lib/websocket'

/**
 * Routing-only envelope — `requestId`/`messageType` are plain strings/literals in
 * every branch of `auroraResponseSchema`, never touched by a date codec, so parsing
 * this is always safe regardless of what codec fields the full payload may contain.
 * `handleIncoming` uses this to decide where a message goes *before* deciding which
 * schema should do the one real decode of its payload.
 */
const incomingMessageEnvelopeSchema = z.object({
  messageType: z.enum(AuroraResponseMessageType),
  requestId: z.string().optional()
})

/**
 * The outcome a dispatch handler returns. Mirrors the wire-level
 * `SubmitDispatchResult.outcome` shape so handlers can describe both
 * successful runs and client-side failures (invalid args, runtime errors,
 * etc.) without the AuroraClient needing to know about the registry.
 */
export type DispatchOutcome =
  | { status: 'success'; value: unknown }
  | { status: 'client_error'; code: string; message: string }

export type DispatchHandler = (args: unknown) => DispatchOutcome | Promise<DispatchOutcome>

type PushHandler = (data: unknown) => void

/**
 * A pending request stores a single dispatch closure that captures the
 * request's typed `resolve`/`reject`/`onIntermediate` from `send()`. This
 * keeps all type-narrowing inside `send()` — the client's incoming message
 * loop just hands the raw (still-undecoded) payload plus its message type to
 * `dispatch` and lets the closure decode it with the one schema that actually
 * owns that shape.
 */
type PendingRequest = {
  cleanup: () => void
  dispatch: (raw: unknown, messageType: AuroraResponseMessageType) => void
}

export class AuroraClient extends ManagedWebsocket {
  static readonly DEFAULT_TIMEOUT_SECONDS = 300

  private readonly pending = new Map<string, PendingRequest>()
  private readonly dispatchHandlers = new Map<string, DispatchHandler>()
  private readonly pushHandlers = new Map<string, PushHandler>()
  private onUnhandledMessage?: (message: AuroraResponse) => void
  private binaryMessageHandler?: (data: ArrayBuffer) => void

  constructor(config: Omit<ManagedWebsocketConfig, 'onMessage' | 'binaryType'>) {
    super({
      ...config,
      onMessage: (event) => this.handleIncoming(event),
      binaryType: 'arraybuffer'
    })
  }

  setOnUnhandledMessage(handler: (message: AuroraResponse) => void): void {
    this.onUnhandledMessage = handler
  }

  /**
   * Register a handler for a server-initiated dispatch action (toolId).
   * The handler receives raw args and returns an outcome describing the
   * call's success or client-side failure. The client packages the
   * outcome into a SubmitDispatchResult reply.
   *
   * Returns an unregister fn so React hooks can clean up on unmount.
   */
  setDispatchHandler(toolId: string, handler: DispatchHandler): () => void {
    this.dispatchHandlers.set(toolId, handler)
    return () => {
      if (this.dispatchHandlers.get(toolId) === handler) {
        this.dispatchHandlers.delete(toolId)
      }
    }
  }

  /**
   * Register a handler for incoming binary frames.
   * Receives the raw ArrayBuffer — callers use parseAuroraBinaryMessage to extract the action and payload.
   * Only one handler can be registered at a time.
   */
  setOnBinaryMessage(handler: (data: ArrayBuffer) => void): () => void {
    this.binaryMessageHandler = handler
    return () => {
      if (this.binaryMessageHandler === handler) {
        this.binaryMessageHandler = undefined
      }
    }
  }

  /**
   * Register a handler for a server-initiated push action. Pushes are
   * fire-and-forget — no reply is sent.
   *
   * Pass a `AuroraPushAction` literal to get a typed `data` parameter narrowed
   * from `AuroraPushEnvelope['data']`. Pass a plain `string` for dynamic
   * registration where the action is not known at compile time.
   */
  setPushHandler<A extends AuroraPushAction>(
    action: A,
    handler: (data: Extract<AuroraPushEnvelope['data'], { action: A }>) => void
  ): () => void
  setPushHandler(action: string, handler: (data: any) => void): () => void {
    // routePush always dispatches message.data, which has already been validated
    // by auroraPushEnvelopeSchema, so the data shape is guaranteed to match the
    // registered action's schema by the time any handler fires.
    this.pushHandlers.set(action, handler)
    return () => {
      if (this.pushHandlers.get(action) === handler) {
        this.pushHandlers.delete(action)
      }
    }
  }

  /**
   * Send a raw binary frame over the socket.
   * Used for Aurora's binary protocol (e.g. streaming audio for speech transcription).
   * Silently drops the frame if the socket is not open - the caller has no meaningful recovery,
   * and transient frame loss during a disconnect is handled by reconnect and a fresh transcription session.
   */
  sendBinary(frame: ArrayBuffer | ArrayBufferView<ArrayBuffer>): void {
    const socket = this.socket
    if (socket === null || socket.readyState !== WebSocket.OPEN) return
    socket.send(frame)
  }

  send<
    RequestSchema extends PanoramaWebSocketApi.RequestPayloadSchema,
    TerminalSchema extends PanoramaWebSocketApi.ResponseEnvelopeSchema<'terminal'>,
    IntermediateSchema extends
      | PanoramaWebSocketApi.ResponseEnvelopeSchema<
          'intermediate',
          PanoramaWebSocketApi.AnyResponsePayloadSchema
        >
      | z.ZodNever
  >(
    definition: PanoramaWebSocketApi.Definition<RequestSchema, TerminalSchema, IntermediateSchema>,
    options: {
      body: Omit<z.input<RequestSchema>, 'action'>
      signal?: AbortSignal
      onIntermediate?: (payload: z.output<IntermediateSchema>['data']) => void
    }
  ): Promise<z.output<TerminalSchema>['data']> {
    type TerminalPayload = z.output<TerminalSchema>['data']
    const { body, signal, onIntermediate } = options

    return new Promise<TerminalPayload>((resolve, reject) => {
      const socket = this.socket
      if (socket === null || !this.isConnected) {
        return reject(new Error('WebSocket is not connected'))
      }

      const requestId = crypto.randomUUID()
      const timeoutSeconds = definition.timeoutSeconds ?? AuroraClient.DEFAULT_TIMEOUT_SECONDS

      const timeout = setTimeout(() => {
        cleanup()
        reject(new Error(`WebSocket request timed out after ${timeoutSeconds}s`))
      }, timeoutSeconds * 1000)

      const cleanup = () => {
        clearTimeout(timeout)
        this.pending.delete(requestId)
        signal?.removeEventListener('abort', onAbort)
      }

      const onAbort = () => {
        cleanup()
        reject(new Error('WebSocket request aborted'))
      }

      signal?.addEventListener('abort', onAbort)

      // Decodes `raw` itself — handleIncoming() only validates the routing envelope
      // (requestId/messageType), so this is the message's one and only real decode,
      // done by the schema that actually owns its shape. That's what lets codec fields
      // (e.g. isoDatetimeToDate) decode correctly instead of being fed a Date twice.
      // handleIncoming() routes Push/Dispatch messages to routePush/routeDispatch
      // before a pending request is ever consulted, so this is never called with
      // those message types.
      const dispatch = (raw: unknown, messageType: AuroraResponseMessageType) => {
        switch (messageType) {
          case AuroraResponseMessageType.Intermediate: {
            if (definition.intermediateSchema === undefined) {
              console.warn('Aurora: received intermediate but API has no intermediateSchema', raw)
              return
            }

            const validation = definition.intermediateSchema.safeParse(raw)
            if (!validation.success) {
              console.warn('Aurora: intermediate failed validation', validation.error)
              return
            }

            onIntermediate?.(validation.data.data)
            return
          }

          case AuroraResponseMessageType.Error: {
            cleanup()
            const validation = errorResponseSchema.safeParse(raw)
            if (!validation.success) {
              reject(validation.error)
              return
            }
            reject(new Error(validation.data.error))
            return
          }

          case AuroraResponseMessageType.Terminal: {
            cleanup()
            const validation = definition.terminalSchema.safeParse(raw)
            if (!validation.success) {
              reject(validation.error)
              return
            }

            resolve(validation.data.data)
            return
          }
        }
      }

      this.pending.set(requestId, { cleanup, dispatch })

      socket.send(
        JSON.stringify({
          requestId,
          data: {
            ...body,
            action: definition.requestSchema.shape.action.value
          }
        })
      )
    })
  }

  /** Reject all pending requests — called on disconnect */
  rejectAllPending(reason: string): void {
    for (const req of this.pending.values()) {
      req.dispatch(
        { requestId: 'UNKNOWN', messageType: AuroraResponseMessageType.Error, error: reason },
        AuroraResponseMessageType.Error
      )
    }
  }

  private handleIncoming(event: MessageEvent): void {
    const data: unknown = event.data
    if (data instanceof ArrayBuffer) {
      this.binaryMessageHandler?.(data)
      return
    }

    if (typeof data !== 'string') {
      console.error('Aurora: failed to parse message', data)
      return
    }

    let raw: unknown
    try {
      raw = JSON.parse(data)
    } catch {
      console.error('Aurora: failed to parse message', data)
      return
    }

    const envelope = incomingMessageEnvelopeSchema.safeParse(raw)
    if (!envelope.success) {
      console.error('Aurora: failed schema validation for message', envelope.error)
      return
    }

    if (envelope.data.messageType === AuroraResponseMessageType.Push) {
      const validation = auroraPushEnvelopeSchema.safeParse(raw)
      if (!validation.success) {
        console.error('Aurora: failed schema validation for message', validation.error)
        return
      }
      this.routePush(validation.data)
      return
    }

    if (envelope.data.messageType === AuroraResponseMessageType.Dispatch) {
      const validation = auroraDispatchEnvelopeSchema.safeParse(raw)
      if (!validation.success) {
        console.error('Aurora: failed schema validation for message', validation.error)
        return
      }
      void this.routeDispatch(validation.data)
      return
    }

    const req = this.pending.get(envelope.data.requestId ?? 'UNKNOWN')

    if (req === undefined) {
      // No pending request to hand the raw payload to for its own decode, so fall
      // back to the full decode here — this is the only place that happens, so it
      // can't collide with a second decode downstream the way dispatch()'s used to.
      const result = auroraResponseSchema.safeParse(raw)
      if (result.success) {
        this.onUnhandledMessage?.(result.data)
      } else {
        console.warn('Aurora: unhandled message failed full schema validation', result.error)
      }
      return
    }

    req.dispatch(raw, envelope.data.messageType)
  }

  private routePush(message: Extract<AuroraResponse, { messageType: 'push' }>): void {
    const handler = this.pushHandlers.get(message.data.action)
    if (handler === undefined) {
      this.onUnhandledMessage?.(message)
      return
    }
    try {
      handler(message.data)
    } catch (error) {
      console.warn('Aurora: push handler threw', { action: message.data.action, error })
    }
  }

  private async routeDispatch(
    message: Extract<AuroraResponse, { messageType: 'dispatch' }>
  ): Promise<void> {
    const { dispatchId, data } = message

    if (data.action !== 'InvokeClientTool') {
      // Defensive: the wire schema currently only defines InvokeClientTool,
      // but the discriminated union may grow. Reply with a client_error so
      // the cloud doesn't hang waiting for a result.
      await this.replyToDispatch(dispatchId, {
        status: 'client_error',
        code: 'unsupported_action',
        message: `Client does not handle dispatch action: ${data.action as string}`
      })
      return
    }

    const handler = this.dispatchHandlers.get(data.toolId)
    if (handler === undefined) {
      await this.replyToDispatch(dispatchId, {
        status: 'client_error',
        code: 'unsupported_tool',
        message: `No handler registered for tool: ${data.toolId}`
      })
      return
    }

    let outcome: DispatchOutcome
    try {
      outcome = await handler(data.args)
    } catch (error) {
      outcome = {
        status: 'client_error',
        code: 'runtime_failure',
        message: error instanceof Error ? error.message : String(error)
      }
    }

    await this.replyToDispatch(dispatchId, outcome)
  }

  private async replyToDispatch(dispatchId: string, outcome: DispatchOutcome): Promise<void> {
    try {
      await this.send(auroraSubmitDispatchResultApi, {
        body: { dispatchId, outcome }
      })
    } catch (error) {
      console.warn('Aurora: failed to send SubmitDispatchResult', { dispatchId, error })
    }
  }
}
